/**
 * validateHeroData · dev-mode diagnostic guardrail (PO 2026-02-11).
 *
 * Called by every Hero consumer to detect drift from the canonical
 * HeroData contract without ever affecting production rendering.
 *
 * Governance contract
 * -------------------
 *   · Diagnostic only. Never throws. Never substitutes data.
 *   · Console warnings are DEV-MODE ONLY (`process.env.NODE_ENV !== "production"`).
 *   · One warning per invalid instance per render cycle. A WeakSet
 *     tracks the data object identity so React StrictMode's
 *     double-invocation does not spam the console.
 *   · Never alters what the consumer rendered. If validation fails,
 *     the family still receives whatever the caller passed.
 *
 * See `/app/memory/HERO_SYSTEM.md` for the canonical contract.
 */

const IS_DEV = process.env.NODE_ENV !== "production";

// Structural helpers ─────────────────────────────────────────
const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const inRange = (n) => typeof n === "number" && n >= 0 && n <= 1;

const KNOWN_SOURCE_TYPES = new Set([
  "ship", "port", "cruise_line", "destination", "editorial", "generic",
]);

/**
 * `validateHeroData(data, context)` — pure. Returns the list of
 * violations found (empty means valid). Callers typically discard
 * the return value; the side-effect of dev-mode warning is the
 * useful part.
 *
 * `context.consumer` — e.g. "WorkspaceHero" | "EditorialHero" —
 * appears in warnings so the drift is trivial to locate.
 * `context.familyExpected` — "operational" | "editorial" |
 * "either" — for family-specific field expectations.
 */
export function validateHeroData(data, context = {}) {
  const violations = [];
  const consumer = context.consumer || "Hero";
  const family = context.familyExpected || "either";

  if (data === null || data === undefined) {
    violations.push(`${consumer}: HeroData is required (received ${data}).`);
    return violations;
  }
  if (!isPlainObject(data)) {
    violations.push(`${consumer}: HeroData must be a plain object.`);
    return violations;
  }

  // image / image_source_type ─────────────────────────────
  if (data.image != null && typeof data.image !== "string") {
    violations.push(`${consumer}: image must be a string URL (got ${typeof data.image}).`);
  }
  if (data.image_source_type != null &&
      !KNOWN_SOURCE_TYPES.has(data.image_source_type)) {
    violations.push(
      `${consumer}: image_source_type must be one of ` +
      `[${Array.from(KNOWN_SOURCE_TYPES).join(", ")}]. ` +
      `Received "${data.image_source_type}".`
    );
  }
  if (data.image && !data.image_source_type) {
    violations.push(
      `${consumer}: image_source_type is missing. ` +
      `Set it so the fallback pipeline can walk the correct ladder ` +
      `when the image fails (defaulting to "generic").`
    );
  }

  // focal_point ────────────────────────────────────────────
  if (data.focal_point != null) {
    if (!isPlainObject(data.focal_point)) {
      violations.push(`${consumer}: focal_point must be an object with { x, y }.`);
    } else {
      if (!inRange(data.focal_point.x)) {
        violations.push(`${consumer}: focal_point.x must be a number between 0 and 1.`);
      }
      if (!inRange(data.focal_point.y)) {
        violations.push(`${consumer}: focal_point.y must be a number between 0 and 1.`);
      }
    }
  }

  // tint ───────────────────────────────────────────────────
  if (data.tint != null && !isPlainObject(data.tint)) {
    violations.push(`${consumer}: tint must be an object.`);
  }

  // alt / title ────────────────────────────────────────────
  if (data.alt != null && typeof data.alt !== "string") {
    violations.push(`${consumer}: alt must be a string.`);
  }
  if (data.image && !data.alt && !data.title) {
    violations.push(
      `${consumer}: neither alt nor title supplied — ` +
      `screen-reader users will lose the image context.`
    );
  }

  // eyebrow ────────────────────────────────────────────────
  if (data.eyebrow != null) {
    if (!isPlainObject(data.eyebrow)) {
      violations.push(`${consumer}: eyebrow must be an object.`);
    } else if (typeof data.eyebrow.label !== "string" || !data.eyebrow.label.trim()) {
      violations.push(`${consumer}: eyebrow.label must be a non-empty string.`);
    }
  }

  // action ─────────────────────────────────────────────────
  if (data.action != null) {
    if (!isPlainObject(data.action)) {
      violations.push(`${consumer}: action must be an object.`);
    } else if (typeof data.action.label !== "string" || !data.action.label.trim()) {
      violations.push(`${consumer}: action.label must be a non-empty string.`);
    }
  }

  // Family-specific soft expectations (warn, don't fail). These
  // never affect rendering; they surface intent drift.
  if (family === "operational" && !data.title) {
    violations.push(`${consumer}: operational heroes should supply a title (ship name).`);
  }
  if (family === "editorial" && !data.title) {
    violations.push(`${consumer}: editorial heroes should supply a title.`);
  }

  return violations;
}


// One-warning-per-instance-per-render bookkeeping. WeakSet keeps
// the map GC-friendly: when the data object is dropped so is its
// warned flag.
const WARNED = new WeakSet();

/**
 * `warnOnHeroDataDrift(data, context)` — dev-mode-only diagnostic
 * helper. Callers spread it at the top of their render:
 *
 *   warnOnHeroDataDrift(data, { consumer: "WorkspaceHero",
 *                                familyExpected: "operational" });
 *
 * Returns void. Never throws. Silent in production.
 */
export function warnOnHeroDataDrift(data, context = {}) {
  if (!IS_DEV) return;
  if (data == null || typeof data !== "object") return;
  if (WARNED.has(data)) return;
  const violations = validateHeroData(data, context);
  if (violations.length === 0) return;
  WARNED.add(data);
  // eslint-disable-next-line no-console
  console.warn(
    "[HeroData validation] " +
    violations.map((v) => `\n  · ${v}`).join("")
  );
}


// Exposed for tests to reset instance memoisation between cases.
export const __resetHeroDataWarnedSetForTests = () => {
  // WeakSet has no .clear(); tests should mint fresh data objects
  // rather than clearing the set. This exists to keep the intent
  // documented — tests DO pass a fresh object per case.
};
